<template>
	<div class="row">
		<div class="col s12 player" ref="plWave">
			<av-waveform
					v-if="canvWidth && isShow"
					:noplayed-line-width="0.8"
					:played-line-width="0.8"
					noplayed-line-color="black"
					played-line-color="#8ef60a"
					playtime-font-color="black"
					:playtime-with-ms="false"
          ref-link="controls"
					:canv-width="canvWidth - 20"
					:canv-height="80"
					ref="waveform"
			/>
		</div>
		<div class="col s12 controls">
      <audio ref="controls" :src="activeSourceUrl || fileUrl" controls preload="auto"></audio>
		</div>
    <div class="col s12 m4 control-group">
      <span class="control-label">Темп x{{ speedRate.toFixed(1) }}</span>
      <input @input="onSpeedChange($event)" type="range" min="0" max="5" step="1" value="5" />
    </div>
    <div class="col s12 m4 control-group">
      <span class="control-label">
        Тональность {{ pendingSemitoneShift > 0 ? '+' + pendingSemitoneShift : pendingSemitoneShift }}
        <small class="control-status">{{ isProcessingPitch ? 'обработка...' : '' }}</small>
      </span>
      <input
        @input="onPitchPreview($event)"
        @change="onPitchCommit($event)"
        type="range"
        min="-7"
        max="7"
        step="1"
        :value="pendingSemitoneShift"
      />
		</div>
    <div class="mt-4 right-align col s12 m4">
			<a class="waves-effect waves-light btn" @click="$emit('onNewFileUpload')">
				Загрузить новый файл
			</a>
		</div>
	</div>
</template>

<script>
let soundTouchLoader = null

export default {
  props: {
    fileUrl: {
      type: [String],
      required: true
    }
  },
  name: 'Player',
  data: () => ({
		canvWidth: null,
    speedRate: 1,
    semitoneShift: 0,
    pendingSemitoneShift: 0,
    isShow: true,
    isProcessingPitch: false,
    audioContext: null,
    originalBuffer: null,
    originalSourceUrl: '',
    processedSourceUrl: '',
    activeSourceUrl: '',
    generatedBlobUrls: [],
    fileLoadToken: 0,
    pitchRenderToken: 0
  }),
  watch: {
    fileUrl: {
      immediate: true,
      handler(newUrl) {
        this.onFileUrlChanged(newUrl)
      }
    }
  },
  methods: {
    getSpeedRateByIndex(value) {
      switch (value) {
        case '5':
          return 1
        case '4':
          return 0.9
        case '3':
          return 0.8
        case '2':
          return 0.7
        case '1':
          return 0.6
        case '0':
          return 0.5
        default:
          return 1
      }
    },
    async ensureAudioContext() {
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
      }
      return this.audioContext
    },
    async ensureSoundTouch() {
      if (window.SoundTouch && window.SimpleFilter) {
        return
      }

      if (!soundTouchLoader) {
        soundTouchLoader = new Promise((resolve, reject) => {
          const existingScript = document.querySelector('script[data-soundtouch="true"]')
          if (existingScript) {
            if (window.SoundTouch && window.SimpleFilter) {
              resolve()
              return
            }

            existingScript.addEventListener('load', () => resolve(), { once: true })
            existingScript.addEventListener('error', reject, { once: true })
            return
          }

          const script = document.createElement('script')
          script.dataset.soundtouch = 'true'
          script.src = '/static/hit_the_notes/js/SoundTouch.js'
          script.onload = () => resolve()
          script.onerror = reject
          document.head.appendChild(script)
        })
      }

      await soundTouchLoader
    },
    async loadDecodedBuffer(url) {
      const audioContext = await this.ensureAudioContext()
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`Не удалось загрузить аудио: ${response.status}`)
      }

      const arrayBuffer = await response.arrayBuffer()
      return audioContext.decodeAudioData(arrayBuffer.slice(0))
    },
    clearActiveBlobUrl() {
      this.generatedBlobUrls.forEach((url) => {
        URL.revokeObjectURL(url)
      })
      this.generatedBlobUrls = []
      this.processedSourceUrl = ''
    },
    async waitForAudioMetadata(audio) {
      if (audio.readyState >= 1) {
        return
      }

      await new Promise((resolve) => {
        const onLoadedMetadata = () => {
          audio.removeEventListener('loadedmetadata', onLoadedMetadata)
          resolve()
        }

        audio.addEventListener('loadedmetadata', onLoadedMetadata, { once: true })
      })
    },
    setPitchPreservation(audio) {
      if (!audio) {
        return
      }

      audio.preservesPitch = true
      audio.mozPreservesPitch = true
      audio.webkitPreservesPitch = true
    },
    async setAudioSource(url, preservePlayback) {
      const audio = this.$refs.controls
      if (!audio || !url) {
        return
      }

      const shouldResume = preservePlayback && !audio.paused && !audio.ended
      const currentTime = preservePlayback ? audio.currentTime : 0

      audio.pause()
      audio.src = url
      this.activeSourceUrl = url
      await this.waitForAudioMetadata(audio)

      if (preservePlayback && Number.isFinite(audio.duration) && audio.duration > 0) {
        audio.currentTime = Math.min(currentTime, Math.max(0, audio.duration - 0.05))
      }

      this.applyPlaybackRate()

      if (shouldResume) {
        audio.play().catch(() => {})
      }
    },
    async encodeAudioBufferToWavBlob(leftChannel, rightChannel, sampleRate) {
      const bytesPerSample = 2
      const numberOfChannels = 2
      const dataLength = leftChannel.length * numberOfChannels * bytesPerSample
      const buffer = new ArrayBuffer(44 + dataLength)
      const view = new DataView(buffer)

      const writeString = (offset, string) => {
        for (let index = 0; index < string.length; index += 1) {
          view.setUint8(offset + index, string.charCodeAt(index))
        }
      }

      const writeSample = (offset, sample) => {
        const clippedSample = Math.max(-1, Math.min(1, sample))
        view.setInt16(offset, clippedSample < 0 ? clippedSample * 0x8000 : clippedSample * 0x7fff, true)
      }

      writeString(0, 'RIFF')
      view.setUint32(4, 36 + dataLength, true)
      writeString(8, 'WAVE')
      writeString(12, 'fmt ')
      view.setUint32(16, 16, true)
      view.setUint16(20, 1, true)
      view.setUint16(22, numberOfChannels, true)
      view.setUint32(24, sampleRate, true)
      view.setUint32(28, sampleRate * numberOfChannels * bytesPerSample, true)
      view.setUint16(32, numberOfChannels * bytesPerSample, true)
      view.setUint16(34, 8 * bytesPerSample, true)
      writeString(36, 'data')
      view.setUint32(40, dataLength, true)

      let offset = 44
      for (let index = 0; index < leftChannel.length; index += 1) {
        writeSample(offset, leftChannel[index])
        offset += 2
        writeSample(offset, rightChannel[index])
        offset += 2
      }

      return new Blob([buffer], { type: 'audio/wav' })
    },
    async renderPitchShiftedSource() {
      if (!this.originalBuffer || !window.SoundTouch || !window.SimpleFilter) {
        return this.originalSourceUrl
      }

      if (this.semitoneShift === 0) {
        return this.originalSourceUrl
      }

      const renderToken = ++this.pitchRenderToken
      const SoundTouch = window.SoundTouch
      const SimpleFilter = window.SimpleFilter

      const soundTouch = new SoundTouch()
      soundTouch.pitchSemitones = this.semitoneShift

      const sourceBuffer = this.originalBuffer
      const leftSource = sourceBuffer.getChannelData(0)
      const rightSource = sourceBuffer.numberOfChannels > 1 ? sourceBuffer.getChannelData(1) : leftSource
      const sourceSound = {
        extract(target, numFrames, position) {
          const availableFrames = Math.max(0, Math.min(numFrames, leftSource.length - position))
          for (let index = 0; index < availableFrames; index += 1) {
            const frameIndex = position + index
            target[index * 2] = leftSource[frameIndex]
            target[index * 2 + 1] = rightSource[frameIndex]
          }
          return availableFrames
        }
      }

      const simpleFilter = new SimpleFilter(sourceSound, soundTouch)
      const chunkSize = 4096
      const chunksLeft = []
      const chunksRight = []
      let totalFrames = 0

      let shouldContinue = true
      while (shouldContinue) {
        if (renderToken !== this.pitchRenderToken) {
          return null
        }

        const target = new Float32Array(chunkSize * 2)
        const framesExtracted = simpleFilter.extract(target, chunkSize)
        if (!framesExtracted) {
          break
        }

        const leftChunk = new Float32Array(framesExtracted)
        const rightChunk = new Float32Array(framesExtracted)
        for (let index = 0; index < framesExtracted; index += 1) {
          leftChunk[index] = target[index * 2]
          rightChunk[index] = target[index * 2 + 1]
        }

        chunksLeft.push(leftChunk)
        chunksRight.push(rightChunk)
        totalFrames += framesExtracted

        if (framesExtracted < chunkSize) {
          shouldContinue = false
        }
      }

      const leftChannel = new Float32Array(totalFrames)
      const rightChannel = new Float32Array(totalFrames)
      let offset = 0

      for (let index = 0; index < chunksLeft.length; index += 1) {
        leftChannel.set(chunksLeft[index], offset)
        rightChannel.set(chunksRight[index], offset)
        offset += chunksLeft[index].length
      }

      const blob = await this.encodeAudioBufferToWavBlob(leftChannel, rightChannel, sourceBuffer.sampleRate)
      if (renderToken !== this.pitchRenderToken) {
        return null
      }

      const blobUrl = URL.createObjectURL(blob)
      this.generatedBlobUrls.push(blobUrl)
      return blobUrl
    },
    async applyCurrentPitchShift(preservePlayback = true) {
      const audio = this.$refs.controls
      if (!audio || !this.originalSourceUrl) {
        return
      }

      this.isProcessingPitch = true
      const wasPlaying = preservePlayback && !audio.paused && !audio.ended
      const currentTime = preservePlayback ? audio.currentTime : 0

      try {
        await this.ensureSoundTouch()
        const nextSourceUrl = await this.renderPitchShiftedSource()
        if (!nextSourceUrl) {
          return
        }

        const previousSourceUrl = this.activeSourceUrl
        if (nextSourceUrl !== previousSourceUrl) {
          await this.setAudioSource(nextSourceUrl, preservePlayback)

          this.processedSourceUrl = nextSourceUrl !== this.originalSourceUrl ? nextSourceUrl : ''
        } else {
          this.applyPlaybackRate()
        }

        if (preservePlayback && Number.isFinite(audio.duration) && audio.duration > 0) {
          audio.currentTime = Math.min(currentTime, Math.max(0, audio.duration - 0.05))
        }

        if (wasPlaying) {
          audio.play().catch(() => {})
        }
      } finally {
        this.isProcessingPitch = false
      }
    },
    async onFileUrlChanged(newUrl) {
      this.fileLoadToken += 1
      const loadToken = this.fileLoadToken

      this.clearActiveBlobUrl()
      this.originalBuffer = null
      this.originalSourceUrl = newUrl || ''
      this.activeSourceUrl = newUrl || ''
      this.semitoneShift = 0
      this.pendingSemitoneShift = 0

      if (!newUrl) {
        return
      }

      try {
        await this.ensureAudioContext()
        this.originalBuffer = await this.loadDecodedBuffer(newUrl)
        if (loadToken !== this.fileLoadToken) {
          return
        }

        await this.applyCurrentPitchShift(false)
      } catch (error) {
        console.error(error)
        this.setAudioSource(newUrl, false)
      }
    },
    applyPlaybackRate() {
      const audio = this.$refs.controls
      if (!audio) {
        return
      }

      this.setPitchPreservation(audio)
      audio.playbackRate = this.speedRate
    },
    onSpeedChange(event) {
      this.speedRate = this.getSpeedRateByIndex(event.target.value)
      this.applyPlaybackRate()
    },
    onPitchPreview(event) {
      this.pendingSemitoneShift = parseInt(event.target.value, 10)
    },
    async onPitchCommit(event) {
      const nextSemitoneShift = parseInt(event.target.value, 10)
      this.pendingSemitoneShift = nextSemitoneShift
      if (nextSemitoneShift === this.semitoneShift) {
        return
      }

      this.semitoneShift = nextSemitoneShift
      await this.applyCurrentPitchShift(true)
    }
  },
	mounted() {
    this.canvWidth = this.$refs.plWave.clientWidth

    const audio = this.$refs.controls
    if (audio) {
      this.setPitchPreservation(audio)
      this.applyPlaybackRate()
    }

		window.onresize = () => {
      this.isShow = false

      this.canvWidth = this.$refs.plWave.clientWidth
      const $el = document.querySelector('canvas')
      $el.width = this.canvWidth

      this.isShow = true
      // Hack for re-render player element
      setTimeout(() => {
        $el.click()
      }, 50)
		}
  },
  beforeDestroy() {
    this.clearActiveBlobUrl()
  }
}
</script>

<style scoped>
	.mt-4 {
		margin-top: 4px;
	}
  .control-group {
    margin-top: 6px;
  }
  .control-label {
    display: block;
    min-height: 29px;
    text-align: center;
    position: relative;
  }
  .control-status {
    display: inline-block;
    min-width: 86px;
    margin-left: 6px;
    font-size: 0.8em;
    position: absolute;
  }
  .control-group input {
    width: 100%;
  }
  .player {
		background-color: #fff;
		border-radius: 5px;
  }
  .player div {
		padding: 10px 0 8px 0;
  }
  .controls {
		margin-top: 5px;
  }
</style>
